<script setup>
import { computed, ref, watch } from 'vue';
import api from 'dashboard/api/aceleres/integrations';
const props = defineProps({ state: { type: Object, required: true } });
const emit = defineEmits(['close', 'refresh']);
const total = ref(0);
const busy = ref(false);
const error = ref('');
const success = ref('');
watch(
  () => props.state,
  state => {
    total.value = state.contract_request?.target_total ?? state.contracted + 1;
  },
  { immediate: true }
);
const difference = computed(() => total.value - props.state.contracted);
const submit = async () => {
  busy.value = true;
  error.value = '';
  const delta = difference.value;
  try {
    emit('refresh', await api.requestKeyContract(total.value));
    success.value =
      delta > 0
        ? `Pedido enviado! Pedimos ${delta} ${delta === 1 ? 'chave a mais' : 'chaves a mais'} para a equipe Zoraxy. Você será avisado nas notificações.`
        : 'Pedido de redução enviado à equipe Zoraxy. Você será avisado nas notificações.';
  } catch (e) {
    error.value = e.message;
  } finally {
    busy.value = false;
  }
};
const cancel = async () => {
  busy.value = true;
  try {
    emit('refresh', await api.cancelKeyContract());
    emit('close');
  } catch (e) {
    error.value = e.message;
  } finally {
    busy.value = false;
  }
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <Teleport to="body">
    <div
      class="fixed inset-0 z-[120] flex items-center justify-center bg-modal-backdrop-dark p-4"
      @click.self="!busy && emit('close')"
    >
      <section
        class="integracoes-contract__dialog w-full max-w-[584px] rounded-[18px] bg-n-solid-1 p-[22px] shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="contract-title"
      >
        <h2 id="contract-title" class="text-lg font-semibold">
          Quantas chaves a empresa precisa?
        </h2>
        <p class="mt-2 !mb-0 !text-[13px] text-n-slate-11">
          Você paga por chave contratada. O pedido vai para a equipe Zoraxy
          liberar.
        </p>
        <div
          class="mt-5 flex items-center justify-between gap-4 rounded-xl bg-n-alpha-2 p-4"
        >
          <div>
            <strong class="text-sm">Chaves contratadas</strong>
            <p class="mt-1 text-xs text-n-slate-11">
              Hoje: {{ state.contracted }} · mínimo {{ state.used }} (em uso ou
              reservadas)
            </p>
          </div>
          <div class="flex items-center gap-3">
            <button
              class="integracoes-contract__decrease !h-9 !w-9 rounded-lg !border !border-solid !border-[#E4E6EA] !p-0 disabled:opacity-40"
              :disabled="busy || total <= state.used"
              @click="total--"
            >
              −
            </button>
            <strong class="text-xl">{{ total }}</strong>
            <button
              class="integracoes-contract__increase !h-9 !w-9 rounded-lg !border !border-solid !border-[#E4E6EA] !p-0"
              :disabled="busy || total >= 1000000"
              @click="total++"
            >
              +
            </button>
          </div>
        </div>
        <p
          class="mt-4 text-sm font-semibold"
          :class="difference < 0 ? 'text-[#9A4B00]' : 'text-[#15803D]'"
        >
          {{ difference > 0 ? '+' : '' }}{{ difference }}
          {{ Math.abs(difference) === 1 ? 'chave' : 'chaves' }} · de
          {{ state.contracted }} para {{ total }}
        </p>
        <p v-if="error" role="alert" class="mt-3 text-sm text-n-ruby-11">
          {{ error }}
        </p>
        <div
          v-if="success"
          role="status"
          class="mt-4 flex gap-3 rounded-xl bg-[#202428] p-4 text-sm text-white"
        >
          <i class="i-lucide-key-round text-[#16A34A]" />{{ success }}
        </div>
        <footer class="mt-6 flex flex-wrap items-center justify-end gap-2">
          <button
            v-if="state.contract_request && !success"
            :disabled="busy"
            class="mr-auto h-10 text-sm text-[#B42318]"
            @click="cancel"
          >
            Cancelar solicitação
          </button>
          <button
            class="integracoes-contract__cancel !h-10 rounded-lg !border !border-solid !border-[#E4E6EA] !px-4 !py-0 !text-[13px]"
            :disabled="busy"
            @click="emit('close')"
          >
            {{ success ? 'Fechar' : 'Cancelar' }}
          </button>
          <button
            v-if="!success"
            class="h-10 rounded-lg bg-[#16A34A] px-4 text-sm font-semibold text-white disabled:opacity-40"
            :disabled="busy || !difference || total < state.used"
            @click="submit"
          >
            {{ state.contract_request ? 'Atualizar pedido' : 'Enviar pedido' }}
          </button>
        </footer>
      </section>
    </div>
  </Teleport>
</template>
